import React, { useEffect, useRef, useState } from "react";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Video, Mic, Square, Link2, Trash2, ScanEye, Copy, AlertTriangle, CheckCircle2 } from "lucide-react";

const VERDICT_META = {
    likely_reading: { label: "⚠ Likely reading from a source", cls: "bg-red-100 text-red-800 border border-red-300" },
    inconclusive: { label: "Inconclusive — some scripted patterns", cls: "bg-amber-100 text-amber-800 border border-amber-300" },
    natural: { label: "✓ Natural, spontaneous delivery", cls: "bg-emerald-100 text-emerald-800 border border-emerald-300" },
};

export default function AIInterview() {
    const [kits, setKits] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [kitOpen, setKitOpen] = useState(false);
    const [kitForm, setKitForm] = useState({ title: "", questions: "" });
    const [recording, setRecording] = useState(false);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [invite, setInvite] = useState({ kit_id: "", candidate_id: "" });
    const [inviteLink, setInviteLink] = useState("");
    const [review, setReview] = useState(null);
    const camRef = useRef(null);
    const streamRef = useRef(null);
    const recRef = useRef(null);
    const chunksRef = useRef([]);
    const fileRef = useRef(null);

    const load = () => {
        api.get("/ai-interview/kits").then((r) => setKits(r.data.items || [])).catch(() => {});
        api.get("/ai-interview/sessions").then((r) => setSessions(r.data.items || [])).catch(() => {});
        api.get("/recruitment/candidates").then((r) => setCandidates(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
    };
    useEffect(() => { load(); return () => streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

    // --- HR records their question video in-browser ---
    const startCam = async () => {
        try {
            const s = await navigator.mediaDevices.getUserMedia({ video: { width: 640 }, audio: true });
            streamRef.current = s;
            if (camRef.current) { camRef.current.srcObject = s; camRef.current.play().catch(() => {}); }
        } catch { toast.error("Camera access denied — you can upload a video file instead."); }
    };
    const startRec = () => {
        if (!streamRef.current) return toast.error("Enable the camera first");
        chunksRef.current = [];
        const rec = new MediaRecorder(streamRef.current, { mimeType: "video/webm" });
        rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
        rec.onstop = () => setRecordedBlob(new Blob(chunksRef.current, { type: "video/webm" }));
        rec.start(1000); recRef.current = rec; setRecording(true);
    };
    const stopRec = () => { recRef.current?.stop(); setRecording(false); };

    const saveKit = async () => {
        if (!kitForm.title.trim()) return toast.error("Give the interview a title");
        const file = recordedBlob ? new File([recordedBlob], "question.webm", { type: "video/webm" }) : fileRef.current?.files?.[0];
        if (!file) return toast.error("Record a question video or upload one");
        const fd = new FormData();
        fd.append("title", kitForm.title);
        fd.append("questions", JSON.stringify(kitForm.questions.split("\n").map((q) => q.trim()).filter(Boolean)));
        fd.append("file", file);
        try {
            await api.post("/ai-interview/kits", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Interview kit saved");
            setKitOpen(false); setKitForm({ title: "", questions: "" }); setRecordedBlob(null);
            streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null;
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Save failed"); }
    };

    const sendInvite = async () => {
        if (!invite.kit_id || !invite.candidate_id) return toast.error("Pick a kit and a candidate");
        try {
            const { data } = await api.post("/ai-interview/invites", invite);
            const url = `${window.location.origin}${data.link}`;
            setInviteLink(url);
            toast.success(`Interview link created for ${data.candidate_name}`);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const openReview = async (s) => {
        try {
            const { data } = await api.get(`/ai-interview/sessions/${s.id}`);
            setReview(data);
        } catch { toast.error("Could not load session"); }
    };

    return (
        <div>
            <PageHeader title="AI Interview" subtitle="Record a question video, send the link — candidates answer on camera. AI transcribes the answer and flags reading-from-a-script."
                actions={
                    <div className="flex items-center gap-2">
                        <button className="h-9 px-3 rounded-md border border-border text-sm font-medium flex items-center gap-2" onClick={() => { setInviteOpen(true); setInviteLink(""); }} data-testid="invite-btn"><Link2 className="size-4" /> Invite candidate</button>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" onClick={() => { setKitOpen(true); setTimeout(startCam, 400); }} data-testid="new-kit-btn"><Plus className="size-4" /> New interview kit</button>
                    </div>
                } />

            <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
                <div>
                    <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><Video className="size-4 text-primary" /> Question kits</h3>
                    <div className="space-y-1.5">
                        {kits.map((k) => (
                            <Surface key={k.id} className="p-2.5">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium">{k.title}</div>
                                    <button className="text-muted-foreground hover:text-destructive" onClick={() => window.confirm(`Delete kit "${k.title}"?`) && api.delete(`/ai-interview/kits/${k.id}`).then(load)}><Trash2 className="size-3.5" /></button>
                                </div>
                                <div className="text-[10px] text-muted-foreground">{(k.questions || []).length} question(s) · {formatDateTime(k.created_at)}</div>
                                <video controls preload="none" className="w-full rounded mt-1.5 max-h-32 bg-black" src={`/api/ai-interview/kits/${k.id}/video`} />
                            </Surface>
                        ))}
                        {!kits.length && <Surface className="p-4"><Empty title="No kits yet" subtitle="Record your first question video." /></Surface>}
                    </div>
                </div>

                <div>
                    <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><ScanEye className="size-4 text-primary" /> Interview sessions</h3>
                    {!sessions.length && <Surface className="p-5"><Empty title="No interviews yet" subtitle="Invite a candidate to a kit." /></Surface>}
                    {sessions.length > 0 && (
                        <TableWrap>
                            <thead><tr><th>Candidate</th><th>Kit</th><th>Status</th><th>Verdict</th><th>Invited</th><th></th></tr></thead>
                            <tbody>
                                {sessions.map((s) => (
                                    <tr key={s.id}>
                                        <td><b>{s.candidate_name}</b></td>
                                        <td className="text-xs">{s.kit_title}</td>
                                        <td><span className={`badge ${s.status === "completed" ? "badge-approved" : "badge-pending"}`}>{s.status}</span></td>
                                        <td>
                                            {s.analysis
                                                ? <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${VERDICT_META[s.analysis.verdict]?.cls}`}>{s.analysis.reading_score} · {s.analysis.verdict.replace(/_/g, " ")}</span>
                                                : <span className="text-[10px] text-muted-foreground">—</span>}
                                        </td>
                                        <td className="text-xs">{formatDateTime(s.invited_at)}</td>
                                        <td>
                                            {s.status === "completed"
                                                ? <button className="h-7 px-2 text-xs rounded-md bg-primary text-white" onClick={() => openReview(s)} data-testid={`review-${s.id}`}>Review</button>
                                                : <button className="h-7 px-2 text-xs rounded-md border border-border" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/interview/${s.token}`); toast.success("Interview link copied"); }}><Copy className="size-3 inline mr-1" />Copy link</button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </TableWrap>
                    )}
                </div>
            </div>

            {/* New kit dialog */}
            <Dialog open={kitOpen} onOpenChange={(v) => { setKitOpen(v); if (!v) { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; setRecordedBlob(null); } }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>New interview kit</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div><Label>Title</Label><Input value={kitForm.title} onChange={(e) => setKitForm({ ...kitForm, title: e.target.value })} placeholder="Senior Accountant — first interview" data-testid="kit-title" /></div>
                        <div><Label>Questions shown to the candidate (one per line)</Label>
                            <textarea className="w-full h-20 rounded-md border border-border bg-background p-2 text-sm" value={kitForm.questions} onChange={(e) => setKitForm({ ...kitForm, questions: e.target.value })} placeholder={"Tell us about yourself.\nWhy do you want this role?"} /></div>
                        <div>
                            <Label>Record your question video</Label>
                            <video ref={camRef} muted playsInline className="w-full rounded-md bg-slate-900 max-h-52" />
                            <div className="flex items-center gap-2 mt-2">
                                {!streamRef.current && <button className="h-8 px-3 rounded-md border border-border text-xs" onClick={startCam}><Video className="size-3 inline mr-1" />Enable camera</button>}
                                {!recording
                                    ? <button className="h-8 px-3 rounded-md bg-red-600 text-white text-xs" onClick={startRec} data-testid="kit-record"><Mic className="size-3 inline mr-1" />{recordedBlob ? "Re-record" : "Record"}</button>
                                    : <button className="h-8 px-3 rounded-md bg-slate-800 text-white text-xs" onClick={stopRec} data-testid="kit-stop"><Square className="size-3 inline mr-1" />Stop</button>}
                                {recordedBlob && !recording && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="size-3" /> recorded</span>}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-1.5">…or upload an existing video: <input ref={fileRef} type="file" accept="video/*" className="text-xs" data-testid="kit-file" /></div>
                        </div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveKit} data-testid="kit-save">Save kit</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Invite dialog */}
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Invite a candidate</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div>
                            <Label>Interview kit</Label>
                            <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={invite.kit_id} onChange={(e) => setInvite({ ...invite, kit_id: e.target.value })} data-testid="invite-kit">
                                <option value="">Pick a kit…</option>
                                {kits.map((k) => <option key={k.id} value={k.id}>{k.title}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Candidate</Label>
                            <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={invite.candidate_id} onChange={(e) => setInvite({ ...invite, candidate_id: e.target.value })} data-testid="invite-candidate">
                                <option value="">Pick a candidate…</option>
                                {candidates.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} · {c.stage}</option>)}
                            </select>
                        </div>
                        {inviteLink && (
                            <div className="border border-emerald-300 bg-emerald-50 rounded-md p-2.5 text-xs">
                                <div className="font-semibold text-emerald-900 mb-1">Send this link to the candidate:</div>
                                <div className="font-mono break-all">{inviteLink}</div>
                                <button className="mt-1.5 h-7 px-2 rounded-md bg-emerald-600 text-white text-[11px]" onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success("Copied"); }}><Copy className="size-3 inline mr-1" />Copy</button>
                            </div>
                        )}
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={sendInvite} data-testid="invite-create">Create interview link</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Review dialog */}
            <Dialog open={!!review} onOpenChange={(v) => !v && setReview(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Interview review — {review?.candidate_name}</DialogTitle></DialogHeader>
                    {review && (
                        <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
                            <video controls className="w-full rounded-lg bg-black max-h-72" src={`/api/ai-interview/sessions/${review.id}/video`} data-testid="answer-video" />
                            {review.engagement && (
                                <Surface className="p-3" data-testid="engagement-panel">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">📊 Engagement timeline</div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                                        {[["Video played", `${review.engagement.video_plays}×`],
                                          ["Watch → record", review.engagement.prep_seconds != null ? `${Math.floor(review.engagement.prep_seconds / 60)}m ${review.engagement.prep_seconds % 60}s` : "—"],
                                          ["Record → submit", review.engagement.record_to_submit_seconds != null ? `${Math.floor(review.engagement.record_to_submit_seconds / 60)}m ${review.engagement.record_to_submit_seconds % 60}s` : "—"],
                                          ["Record attempts", review.engagement.record_attempts ?? "—"]].map(([l, v]) => (
                                            <div key={l} className="border border-border rounded-md p-1.5"><div className="text-[9px] uppercase text-muted-foreground">{l}</div><div className="font-mono font-semibold text-sm">{v}</div></div>
                                        ))}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-1.5">
                                        First watched {review.engagement.first_played_at ? formatDateTime(review.engagement.first_played_at) : "—"} → started answering {review.engagement.recording_started_at ? formatDateTime(review.engagement.recording_started_at) : "—"} → submitted {review.engagement.submitted_at ? formatDateTime(review.engagement.submitted_at) : "—"}.
                                        {review.engagement.prep_seconds > 600 && <b className="text-amber-700"> Long prep gap — possible script preparation.</b>}
                                    </div>
                                </Surface>
                            )}
                            {review.analysis && (
                                <Surface className="p-3">
                                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${VERDICT_META[review.analysis.verdict]?.cls}`}>{VERDICT_META[review.analysis.verdict]?.label}</span>
                                        <span className="text-sm">Reading score: <b className="font-mono">{review.analysis.reading_score}/100</b></span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center mb-2">
                                        {[["Words", review.metrics?.words], ["WPM", review.metrics?.wpm], ["Fillers /100w", review.metrics?.fillers_per_100_words], ["Pauses /min", review.metrics?.long_pauses_per_min]].map(([l, v]) => (
                                            <div key={l} className="border border-border rounded-md p-1.5"><div className="text-[9px] uppercase text-muted-foreground">{l}</div><div className="font-mono font-semibold text-sm">{v ?? "—"}</div></div>
                                        ))}
                                    </div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Why</div>
                                    <ul className="text-xs space-y-1">
                                        {(review.analysis.signals || []).map((s, i) => <li key={i} className="flex gap-1.5"><AlertTriangle className="size-3 text-amber-500 shrink-0 mt-0.5" />{s}</li>)}
                                    </ul>
                                    {review.analysis.llm_opinion && <div className="mt-2 text-xs border-t border-border pt-2"><b>Claude's opinion:</b> {review.analysis.llm_opinion}</div>}
                                </Surface>
                            )}
                            <Surface className="p-3">
                                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Transcript</div>
                                <div className="text-sm whitespace-pre-wrap">{review.transcript || "No speech was transcribed (the candidate's browser may not support live transcription)."}</div>
                            </Surface>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
